import * as React from "react";

import { useDimensions } from "../../../../HiddenPanel/ReactEffects";
import { useAsyncEffect } from "../../../../../../../../Common/UseAsyncEffect";
import { nextTick } from "../../../../../../../../Common/AsyncUtils";

import { AnimationWrapper, PropsToChildrenWrapper } from "./VerticalSlideInCollapsibleBlock.styles";
import { CollapsibleChildren } from "./PropsToChildren";

interface VerticalSlideInCollapsibleBlockProps {
    collapsed: boolean;
    children: CollapsibleChildren;
}

export function VerticalSlideInCollapsibleBlock(props: VerticalSlideInCollapsibleBlockProps): JSX.Element {
    const [content, contentRect] = useDimensions({ liveMeasure: false }, [props.collapsed]);
    const [collapsedState, setCollapsedState] = React.useState(props.collapsed);
    const [inTransition, setInTransition] = React.useState(false);
    const isFirstRun = React.useRef(true);

    const beginTransition = React.useCallback(() => {
        if (isFirstRun.current) {
            isFirstRun.current = false;
            return;
        }
        if (!inTransition) {
            setInTransition(true);
        }
    }, [props.collapsed, inTransition]);

    React.useEffect(beginTransition, [props.collapsed]);

    useAsyncEffect(async () => {
        if (inTransition) {
            await nextTick();
            setCollapsedState(props.collapsed);
        }
    }, [props.collapsed, inTransition]);

    const handleTransitionEnd = () => {
        setInTransition(false);
    };

    return (
        <AnimationWrapper
            collapsed={collapsedState}
            onTransitionEnd={handleTransitionEnd}
            inTransition={inTransition}
            tempHeight={contentRect?.height}>
            <PropsToChildrenWrapper ref={content}>{props.children}</PropsToChildrenWrapper>
        </AnimationWrapper>
    );
}
